import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '../../api/api';
import { PageDataProvider } from '../PageDataContext';
import { processTimeSeriesData } from '../../utils/fisheriesTimeSeriesProcessor';
import { calculateInsights } from '../../utils/fisheriesInsightsCalculator';
import { mapLobsterAnnualResultsToFisheryData } from '../../utils/lobsterFisheryDataMapper';

/** Static fishery stats per plan. Used by CommercialCard via context. */
const PLAN_FISHERY_STATS: Record<string, Record<string, unknown> | null> = {
  'ne-multi-groundfish': {
    year: 'aggregate of 2015-2024',
    landedExVesselValue: 500_700_000,
    missedRevenueToFishermen: 2_459_300_000,
    landedEconomicImpact: 2_294_000_000,
    jobs: 13_192,
    landedMeals: 398_600_000,
  },
  'sea-scallop': {
    year: '2024',
    landedExVesselValue: 313_000_000,
    missedRevenueToFishermen: undefined,
    landedEconomicImpact: 1_434_036_349,
    jobs: undefined,
    landedMeals: 60_800_000,
  },
};

export interface PlanDataProviderProps {
  children: React.ReactNode;
  regionSlug?: string;
  planSlug?: string;
}

export function PlanDataProvider({ children, regionSlug, planSlug }: PlanDataProviderProps): React.ReactElement {
  const { getPlansBySlugs, getPageChildren, getYears, getFisheriesDataBatch, getLobsterScenarios, getLobsterScenario } = useApi();
  const [timeSeriesData, setTimeSeriesData] = useState<Record<string, unknown[]>>({
    biomass: [],
    impact: [],
    meals: [],
    exvessel: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lobsterInsights, setLobsterInsights] = useState<Record<string, unknown> | null>(null);
  const [lobsterResults, setLobsterResults] = useState<{ annual_results?: unknown[] } | null>(null);
  const [lobsterLoading, setLobsterLoading] = useState(false);

  const fisheryStats = (planSlug && PLAN_FISHERY_STATS[planSlug]) ?? null;
  const fisheryStatsInsights = fisheryStats
    ? {
        exVesselValue: fisheryStats.landedExVesselValue as number | undefined,
        missedValue: fisheryStats.missedRevenueToFishermen as number | undefined,
        economyValue: fisheryStats.landedEconomicImpact as number | undefined,
        totalJobs: fisheryStats.jobs as number | undefined,
        mealsProduced: fisheryStats.landedMeals as number | undefined,
      }
    : null;
  const insights = lobsterInsights ?? fisheryStatsInsights;
  const data: Record<string, unknown> = {
    fisheryStats,
    timeSeriesData,
    insights,
    originalInsights: insights,
    lobsterLoading,
    lobsterResults,
    selectedModel: planSlug === 'american-lobster' ? 'LOBSTER' : undefined,
  };

  const fetchData = useCallback(async () => {
    if (planSlug !== 'ne-multi-groundfish') {
      setLoading(false);
      return;
    }

    if (!regionSlug || !planSlug) return;

    try {
      setLoading(true);
      setError(null);

      const [plansResponse, yearsResponse] = await Promise.all([
        getPlansBySlugs(regionSlug, planSlug),
        getYears(),
      ]);

      const plan = plansResponse?.data;
      const planData = Array.isArray(plan) ? plan[0] : plan;
      const planId = planData?.fishery_management_plan_id ?? planData?.id ?? null;
      const allYears = yearsResponse.data || [];

      if (!planId || allYears.length === 0) {
        setLoading(false);
        return;
      }

      const childrenResponse = await getPageChildren('plan', planId);
      const children = childrenResponse?.data ?? [];
      const stockIds = (Array.isArray(children) ? children : [])
        .map((child: { resource_id?: number; id?: number }) => child.resource_id ?? child.id)
        .filter((id): id is number => id != null && typeof id === 'number');

      if (stockIds.length === 0) {
        setLoading(false);
        return;
      }

      const response = await getFisheriesDataBatch(allYears, stockIds);
      const allData = response.data.data;

      const yearDataMap: Record<number, { year: number; data: unknown[] }> = {};
      allYears.forEach((year: number) => {
        yearDataMap[year] = { year, data: [] };
      });

      allData.forEach((item: { year: number }) => {
        if (yearDataMap[item.year]) {
          yearDataMap[item.year].data.push(item);
        }
      });

      const allYearData = Object.values(yearDataMap);
      const { biomass, impact, meals, exvessel } = processTimeSeriesData(
        allYearData,
        null,
        false
      );

      setTimeSeriesData({ biomass, impact, meals, exvessel });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [regionSlug, planSlug, getPlansBySlugs, getPageChildren, getYears, getFisheriesDataBatch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Lobster data load (for american-lobster plan page)
  useEffect(() => {
    console.log('[LOBSTER PlanDataProvider] useEffect', { planSlug, willLoad: planSlug === 'american-lobster' });
    if (planSlug !== 'american-lobster') return;

    const loadLobsterData = async () => {
      console.log('[LOBSTER PlanDataProvider] loadLobsterData starting');
      try {
        setLobsterLoading(true);
        const response = await (getLobsterScenarios as (limit?: number | null) => Promise<{ data?: unknown; results?: unknown[] }>)(10);
        console.log('[LOBSTER PlanDataProvider] getLobsterScenarios response', { response, scenariosCount: Array.isArray(response?.data) ? response.data.length : (response?.data as { results?: unknown[] })?.results?.length ?? 0 });
        let scenarios: unknown[] = [];
        if (Array.isArray(response?.data)) {
          scenarios = response.data;
        } else if (response?.data && typeof response.data === 'object' && Array.isArray((response.data as { results?: unknown[] }).results)) {
          scenarios = (response.data as { results: unknown[] }).results;
        }
        if (scenarios.length > 0) {
          const latestScenario = scenarios[0] as { id: number };
          console.log('[LOBSTER PlanDataProvider] fetching scenario', { id: latestScenario.id });
          const scenarioData = await getLobsterScenario(latestScenario.id);
          console.log('[LOBSTER PlanDataProvider] getLobsterScenario result', { hasData: !!scenarioData?.data, hasAnnualResults: !!scenarioData?.data?.annual_results });
          if (scenarioData?.data?.annual_results) {
            const lobsterFisheryData = mapLobsterAnnualResultsToFisheryData(
              scenarioData.data.annual_results as Parameters<typeof mapLobsterAnnualResultsToFisheryData>[0]
            );
            const lobsterDataAsRecords = lobsterFisheryData as unknown as Record<string, unknown>[];
            const opts = {
              selectedStockId: null,
              selectedModel: 'LOBSTER',
              lobsterResults: scenarioData.data as {
                results?: Array<{ fishery_revenue_millions?: number }>;
                annual_results?: Record<string, unknown>[];
              } | null,
            };
            const fullInsights = calculateInsights(lobsterDataAsRecords, { ...opts, forPeriod: 'full' });
            setLobsterInsights(fullInsights);
            setLobsterResults({ annual_results: scenarioData.data.annual_results });
          }
        } else {
          console.log('[LOBSTER PlanDataProvider] no scenarios found, clearing data');
          setLobsterInsights(null);
          setLobsterResults(null);
        }
      } catch (err) {
        console.error('[LOBSTER PlanDataProvider] Error loading lobster data:', err);
        setLobsterInsights(null);
      } finally {
        setLobsterLoading(false);
      }
    };
    loadLobsterData();
  }, [planSlug, getLobsterScenarios, getLobsterScenario]);

  const value = {
    data,
    loading,
    error,
    actions: {} as Record<string, (...args: unknown[]) => void | Promise<void>>,
  };

  return <PageDataProvider value={value}>{children}</PageDataProvider>;
}
